import React, { useState } from 'react';
import {
  // shallowEqual,
  useDispatch,
  // useSelector
} from 'react-redux';
import * as campaignActions from 'actions/campaign';
import * as routerActions from 'router/actions';
import { useI18n } from 'components/I18n';
import {
  PaymentProviderFooter,
  AdyenResultHandler,
} from 'components/Payment';
import {
  Box,
  Heading,
  Text,
  Metadata,
  SecondaryButton,
} from 'components';
import NoOffersErrorPage from 'components/ErrorPage/NoOffersErrorPage';
import { RouterLocation } from 'router';
import {
  AppDispatch,
  // RootState
} from 'reducers';
import { Offer } from 'types';
import { flattenEdges } from 'utils/helpers';
import ErrorPage, { ERROR_ICONS } from 'components/ErrorPage/ErrorPage';
import PromoCodeModal from '../PromoCodeModal';
import { useGetOffersToChangeQuery } from '../getOffersToChange.generated';
import OfferComponent from './Offer';
import ChangePlanModal from '../ChangePlanModal';
import { Container } from './styles';

type Props = {
  location: RouterLocation,
};

// Select offer component to change user's entitlement offer. Can be opened from My Plan page
// Route: '/offers/change/:id' where id is entitlement's offerId
function SelectOffersChange({ location }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const i18n = useI18n();

  const { id } = location.params!;

  // const {
  //   withPromoCodes,
  // } = useSelector((state: RootState) => ({
  //   withPromoCodes: (
  //     state.settings.features.payment?.allowPromoCodes
  //     && state.settings.features.payment?.allowUpgradePromoCodes
  //   ) || false,
  // }), shallowEqual);
  const withPromoCodes = false; // temporary disable promo code button

  const [newOffer, setNewOffer] = useState<Offer | null>(null);
  const [showModal, setShowModal] = useState(false);

  const {
    data,
    error: apolloError,
  } = useGetOffersToChangeQuery({ variables: { offerId: id } });

  if (apolloError) {
    return (
      <ErrorPage button="retry" icon={ERROR_ICONS.NOT_FOUND}>
        <Text id="error" />
      </ErrorPage>
    );
  }

  if (!data) {
    return null;
  }

  const originalOffer = data.viewer.offer as Offer;
  // find availableOffers from subscriptions.replacements API
  // only UPGRADE ones for now
  const availableOffers = flattenEdges(
    data.viewer.subscriptions?.edges?.find(s => s?.node.offerId === id)?.node.replacements || [],
  ).filter((s: Offer) => s?.subscribeKind === 'UPGRADE') as Offer[];

  if (!originalOffer || !availableOffers.length) {
    return (
      <NoOffersErrorPage hasEntitlement />
    );
  }

  const validatePromocode = async (newPromoCode: string) => {
    const expectedOfferIds = availableOffers.map((offer: Offer) => offer.id);

    try {
      const { offerIds } = await dispatch(campaignActions.getCampaign(newPromoCode));

      const matchingOfferIds = expectedOfferIds.filter((oId: string) => offerIds.includes(oId));

      if (!matchingOfferIds.length) {
        throw { errorId: 'promoCode.notValidForOffer' };
      }

      const offerId = matchingOfferIds[0];

      const { valid, errorId } = await dispatch(campaignActions.validatePromocodeToOffer(offerId, newPromoCode));

      if (!valid) {
        throw { errorId };
      }

      dispatch(routerActions.push({
        name: 'checkout-upgrade',
        params: { id: offerId, originalOfferId: originalOffer.id },
        query: { promoCode: newPromoCode },
      }));
    } catch (e: any) {
      throw i18n.formatText(
        e?.errorId || campaignActions.extractPromocodeValidationError(JSON.stringify(e)),
        { promoCode: newPromoCode },
      );
    }
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const onOfferClick = (offer: Offer) => {
    setNewOffer(offer);
  };

  const onChangeOfferConfirm = (offer: Offer) => {
    dispatch(routerActions.push({
      name: 'checkout-upgrade',
      params: { id: offer.id, originalOfferId: originalOffer.id },
    }));
  };

  return (
    <Container column mx="auto" mt="xxxlarge" maxWidth="100%">
      <Metadata title="checkout.changeOffer" />

      <Box mb="medium">
        <AdyenResultHandler />
      </Box>

      <Box
        mb="xlarge"
        maxWidth="100%"
      >
        <Heading
          fontSize="sectionHeading"
          align="center"
          id="checkout.changeOffer"
        />

        <Box mt="xlarge" row wrap columnGap="xlarge">
          <OfferComponent
            key={originalOffer.id}
            offer={originalOffer}
            btnText="payment.currentOffer"
            onClick={() => {}}
            disabled
          />
          {availableOffers.map(offer => (
            <OfferComponent
              key={offer.id}
              offer={offer}
              onClick={() => onOfferClick(offer)}
            />
          ))}
        </Box>
      </Box>

      {withPromoCodes && (
        <Box mb="large">
          <SecondaryButton
            variant="white"
            onClick={() => setShowModal(true)}
            className="e2e-promocode-cta"
          >
            <Text id="payment.promoCodeButton" />
          </SecondaryButton>

          {showModal && (
            <PromoCodeModal
              validatePromocode={validatePromocode}
              onClose={hideModal}
            />
          )}
        </Box>
      )}

      <Box mb="small">
        <PaymentProviderFooter />
      </Box>

      {newOffer && (
        <ChangePlanModal
          currentOffer={originalOffer}
          newOffer={newOffer}
          onDismiss={() => setNewOffer(null)}
          onConfirm={onChangeOfferConfirm}
        />
      )}
    </Container>
  );
}

export default React.memo(SelectOffersChange);
